const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Creates a deep clone of a value.
 * *NOTE: Mimics lodash cloneDeep behavior.*
 * Handles primitives, arrays, plain objects, Date, Map, Set, RegExp,
 * Buffer, TypedArray, Error, and circular references.
 *
 * @param value - The value to clone.
 * @returns A deep clone of the value.
 */
export function cloneDeep<T>(value: T): T {
  const seen = new WeakMap();

  function clone(val: unknown): unknown {
    // Primitives and null
    if (val === null || typeof val !== 'object') {
      return val;
    }

    // Handle circular references
    if (seen.has(val)) {
      return seen.get(val);
    }

    // Handle Date
    if (val instanceof Date) {
      return new Date(val.getTime());
    }

    // Handle RegExp
    if (val instanceof RegExp) {
      return new RegExp(val.source, val.flags);
    }

    // Handle Map
    if (val instanceof Map) {
      const result = new Map();
      seen.set(val, result);
      val.forEach((v, k) => result.set(clone(k), clone(v)));
      return result;
    }

    // Handle Set
    if (val instanceof Set) {
      const result = new Set();
      seen.set(val, result);
      val.forEach((v) => result.add(clone(v)));
      return result;
    }

    // Handle Array
    if (Array.isArray(val)) {
      const result: unknown[] = [];
      seen.set(val, result);
      for (let i = 0; i < val.length; i++) {
        result[i] = clone(val[i]);
      }
      return result;
    }

    // Handle plain objects and class instances
    const result: Record<string, unknown> = {};
    seen.set(val, result);
    for (const key of Object.keys(val)) {
      result[key] = clone((val as Record<string, unknown>)[key]);
    }
    return result;
  }

  return clone(value) as T;
}

/**
 * Deeply merges multiple objects into a single object.
 * *NOTE: Mimics lodash merge behavior.*
 * This will mutate the target object.
 *
 * @param target - The target object to merge into.
 * @param sources - The source objects to merge from.
 * @returns The merged object. If no sources are provided, the target object is returned.
 */
export function deepMerge<T extends object>(target: T, ...sources: Partial<T>[]): T {
  const seen = new WeakMap<object, object>();

  function merge(t: Record<string, unknown>, s: Record<string, unknown>): Record<string, unknown> {
    if (seen.has(s)) { return seen.get(s) as Record<string, unknown>; }
    seen.set(s, t);

    for (const key in s) {
      if (UNSAFE_KEYS.has(key)) { continue; }

      const sourceValue = s[key];

      // Skip undefined values (lodash behavior)
      if (sourceValue === undefined) { continue; }

      const targetValue = t[key];

      if (Array.isArray(sourceValue)) {
        // Merge arrays by index (lodash behavior)
        const targetArray = Array.isArray(targetValue) ? targetValue : [];
        t[key] = mergeArrays(targetArray, sourceValue);
      }
      else if (isPlainObject(sourceValue)) {
        t[key] = merge(isPlainObject(targetValue) ? targetValue : {}, sourceValue);
      }
      else {
        t[key] = sourceValue;
      }
    }
    return t;
  }

  function mergeArrays(targetArray: unknown[], source: unknown[]): unknown[] {
    const result = [...targetArray];
    source.forEach((val, i) => {
      if (val === undefined) { return; }

      if (Array.isArray(val)) {
        result[i] = mergeArrays(Array.isArray(result[i]) ? result[i] as unknown[] : [], val);
      }
      else if (isPlainObject(val)) {
        result[i] = merge(isPlainObject(result[i]) ? result[i] as Record<string, unknown> : {}, val);
      }
      else {
        result[i] = val;
      }
    });
    return result;
  }

  for (const source of sources) {
    if (source !== null && source !== undefined && typeof source === 'object') {
      merge(target as Record<string, unknown>, source as Record<string, unknown>);
    }
  }

  return target;
}

/**
 * Checks if a value is a plain object (not an array, Date, Map, Set, etc.)
 */
export function isPlainObject(item: unknown): item is Record<string, unknown> {
  if (item === null || typeof item !== 'object' || Array.isArray(item)) {
    return false;
  }
  const proto = Object.getPrototypeOf(item);
  return proto === null || proto === Object.prototype;
}

/** Throws when `value` is `undefined`; otherwise returns it with `undefined` removed from the type. */
export function requireDefined<T>(value: T | undefined, message = 'Expected value to be defined'): T {
  if (value === undefined) {
    throw new Error(message);
  }
  return value;
}

/**
 * Reads the listed keys from a partial object and returns them as required.
 * Throws on the first missing (`undefined`) key.
 */
export function requireKeys<T extends object, K extends keyof T>(
  data: Partial<T>,
  keys: readonly K[],
  messagePrefix = 'Missing required key'
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    result[key] = requireDefined<T[K]>(data[key], `${messagePrefix}: ${String(key)}`);
  }
  return result;
}

/**
 * Recursively assigns default properties where target value is `undefined`.
 * *NOTE: Mimics lodash defaultsDeep behavior.*
 * This will mutate the target object.
 *
 * @param target - The target object to assign defaults to.
 * @param sources - The source objects containing default values.
 * @returns The target object with defaults applied.
 */
export function defaultsDeep<T extends object>(target: T, ...sources: object[]): T {
  const seen = new WeakSet();

  function applyDefaults(t: Record<string, unknown>, s: Record<string, unknown>): void {
    if (seen.has(s)) { return; }
    seen.add(s);

    for (const key of Object.keys(s)) {
      if (UNSAFE_KEYS.has(key)) { continue; }

      const targetVal = t[key];
      const sourceVal = s[key];

      if (targetVal === undefined) {
        t[key] = sourceVal;
      }
      else if (isPlainObject(targetVal) && isPlainObject(sourceVal)) {
        applyDefaults(targetVal, sourceVal);
      }
    }
  }

  for (const source of sources) {
    if (isPlainObject(source)) {
      applyDefaults(target as Record<string, unknown>, source);
    }
  }
  return target;
}
